import { InvalidCatalogDefinitionError } from "./errors.model";
import type { ObservationLevel } from "./lib/observation-level";
import type { PlannedTrial, PlannedTrialId } from "./lib/planned-trial";
import {
  assertDuration,
  distinctExams,
  normalizeProtocolContent,
  type PlacedProtocolStep,
  placementFor,
  serializePlacement,
  serializeStep,
} from "./lib/protocol-content";
import {
  assignMemberIds,
  type Member,
  type MemberId,
  type MemberIdSequence,
  memberIdSequence,
  memberIds,
} from "./member-identity";
import {
  assertUnpinnedReference,
  CatalogDefinition,
  type CatalogSpine,
  type CompletenessViolation,
  type ReferenceAvailabilityFacts,
} from "./spine.model";
import type { StepPlacementInput, StepPlacementProps } from "./step-placement";
import { answeredValueProblem, type ParameterAnswer, type ParameterValue } from "./value.model";

export { ENVELOPE_DURATION_CAP_MINUTES, ENVELOPE_DURATION_INCREMENT_MINUTES } from "./envelope-duration";

// ============================================================
// ProtocolDefinition — the body of clinical work for one visit
//
// TWO COLLECTIONS, NOT ONE LIST. Ordered `steps` hold the timed occurrences of
// treatments and diagnostics; a flat, unordered `exams` set holds untimed work
// with no scheduling consequence. They are not one list with an optional
// duration, because "what is the sequence position of an exam" has no answer
// and a zero duration is a lie something will eventually sum, validate or
// render as "0 min". Keeping exams out of the timed structure means THE
// DURATION CALCULATION CANNOT SEE SOMETHING IT SHOULD IGNORE.
//
// The knock-on is in the types: a step holds a `ProcedureRef` and the exam set
// holds an `ExamRef`, so an exam cannot end up in a scheduling slot. Both are
// canonical urls; branding is what makes the mistake unrepresentable rather
// than merely wrong.
//
// Cost accepted: exams have no notion of WHEN during the visit they happen.
// "Do the exam before the diagnostics" is not expressible.
//
// CONCURRENCY, UNCHANGED (ADR-0010): steps are sequential and occurrences
// within a step run together. Each step owns its placement and duration; the
// protocol reserves through the latest step end, including gaps and authored step durations.
//
// NOT INCLUDED: occurrence `title` and `description` — a protocol renaming a
// diagnostic means the same scan appears under three names across three
// protocols and a clinician cannot match an order back to the catalog entry.
// Occurrence `resourceRequirements` — requirements are declared by the
// definition, which knows what it needs; an occurrence-level requirement lets
// a protocol demand a device the diagnostic never declared. `encounterClass`,
// `memberDurations`, and the `type` coding that used to discriminate protocols
// from programs (the discriminator is the mapper's, never a domain field).
// ============================================================

const OCCURRENCE_PREFIX = "occ";
const OCCURRENCES = "occurrence";

declare const procedureRefBrand: unique symbol;
declare const examRefBrand: unique symbol;

/** An unpinned canonical url naming a TreatmentDefinition or DiagnosticDefinition lineage. */
export type ProcedureRef = string & { readonly [procedureRefBrand]: true };

/** An unpinned canonical url naming an ExamDefinition lineage. */
export type ExamRef = string & { readonly [examRefBrand]: true };

/**
 * References inside the catalog identify a LINEAGE and always resolve to its
 * current revision, so a version-pinned url is refused here rather than
 * quietly freezing a protocol to a revision the author never chose. Pins exist
 * only on the output side, on the work produced.
 */
export function procedureRef(url: string): ProcedureRef {
  return assertUnpinnedReference(url) as ProcedureRef;
}

export function examRef(url: string): ExamRef {
  return assertUnpinnedReference(url) as ExamRef;
}

/**
 * WHY AN OCCURRENCE IS THERE. `authored` means someone placed it; an `exam`
 * entry means an exam's finding declared it and the chain must not be broken.
 * A set, because a scan can be both — and that one survives an exam removal,
 * correctly.
 */
export type OccurrenceJustification = { kind: "authored" } | { kind: "exam"; exam: ExamRef };

const AUTHORED: OccurrenceJustification = { kind: "authored" };

/** One timed occurrence of a treatment or diagnostic within a step. */
export interface ProtocolOccurrence {
  id: MemberId;
  definition: ProcedureRef;
  /**
   * A SUFFIX, NOT A NAME. The author types "Left Side" and the UI shows
   * "Treatment A — Left Side"; they never type "Treatment A". A whole-name
   * field would freeze a copy of the definition's name into the protocol,
   * which goes stale the moment the definition is renamed — the exact
   * staleness the lineage-level name rule exists to prevent.
   */
  qualifier?: string;

  parameterValues: readonly ParameterAnswer[];
  observationLevels?: readonly ObservationLevel[];
  /** Absent when none. An occurrence with planned trials answers nothing itself (Rule 7). */
  plannedTrials?: readonly PlannedTrial[];
  justifications: readonly OccurrenceJustification[];
}

/** A group of concurrent occurrences. Steps themselves are sequential. */
export interface ProtocolStep {
  placement: StepPlacementProps;
  title?: string;
  occurrences: readonly ProtocolOccurrence[];
}

/**
 * What a caller hands `setSteps`. It CANNOT carry justifications: those are
 * the entity's own bookkeeping, and a caller able to write them could claim an
 * exam requires an occurrence it never asked for, or drop the claim that makes
 * the exam-diagnostic chain unbreakable.
 */
interface ProtocolOccurrenceInput extends Member {
  definition: ProcedureRef;
  qualifier?: string;
  parameterValues?: readonly ParameterAnswer[];
  observationLevels?: readonly ObservationLevel[];
  plannedTrials?: readonly PlannedTrial[];
}

export interface ProtocolStepInput {
  placement: StepPlacementInput;
  title?: string;
  occurrences: readonly ProtocolOccurrenceInput[];
}

/** A new occurrence may request to fulfill one exam declaration; it cannot supply provenance. */
export interface ProtocolWorkInput {
  envelopeMinutes: number;
  steps: readonly {
    placement: StepPlacementInput;
    title?: string;
    occurrences: readonly ProtocolOccurrenceInput[];
  }[];
  exams: readonly ExamRef[];
}

export interface ProtocolWorkFacts extends ReferenceAvailabilityFacts {}

/** The stored shape: hydration supplies ids and justifications, a fresh build supplies neither. */
interface ProtocolOccurrenceProps extends ProtocolOccurrenceInput {
  justifications?: readonly OccurrenceJustification[];
}

interface ProtocolStepProps {
  placement: StepPlacementProps;
  title?: string;
  occurrences: readonly ProtocolOccurrenceProps[];
}

export interface ProtocolDefinitionProps {
  spine: CatalogSpine;
  envelopeMinutes: number;
  steps: readonly ProtocolStepProps[];
  exams: readonly ExamRef[];
  /**
   * The occurrence id high-water mark. Aggregate state that must be persisted:
   * seeding it from the live occurrences would reuse the id of a removed one
   * the moment the removed one was the highest.
   */
  occurrenceHighWaterMark?: number;
}

/** What the mapper reads. A copy — handing out live state would let a caller edit around the commands. */
export interface ProtocolDefinitionFacts {
  spine: CatalogSpine;
  envelopeMinutes: number;
  steps: readonly ProtocolStep[];
  exams: readonly ExamRef[];
  occurrenceHighWaterMark: number;
}

export class ProtocolDefinition extends CatalogDefinition<ProtocolDefinition> {
  private envelopeMinutes: number;
  private steps: PlacedProtocolStep[];
  private exams: ExamRef[];
  // A number rather than a `MemberIdSequence`: commands mint from a fresh
  // sequence seeded here and write the high-water mark back.
  private occurrenceHigh: number;

  constructor(props: ProtocolDefinitionProps) {
    super(props.spine);
    const content = normalizeProtocolContent(props);
    this.envelopeMinutes = content.envelopeMinutes;
    this.steps = content.steps;
    this.exams = distinctExams(props.exams);
    this.occurrenceHigh = content.occurrenceHigh;
  }

  // ── facts ──

  /** Every occurrence across every step, in protocol order. */
  get occurrences(): readonly ProtocolOccurrence[] {
    return structuredClone(this.steps.flatMap((step) => step.occurrences));
  }

  get examRefs(): readonly ExamRef[] {
    return [...this.exams];
  }

  /** The reservation includes every step's exclusive end, including gaps and authored step durations. */
  get durationMinutes(): number {
    return Math.max(this.envelopeMinutes, ...this.steps.map((step) => step.placement.endMinutes));
  }

  snapshot(): ProtocolDefinitionFacts {
    return this.facts;
  }

  get facts(): ProtocolDefinitionFacts {
    return structuredClone({
      spine: this.spineFacts,
      envelopeMinutes: this.envelopeMinutes,
      steps: this.steps.map(serializeStep),
      exams: this.exams,
      occurrenceHighWaterMark: this.occurrenceHigh,
    });
  }

  protected copyForRevision(spine: CatalogSpine): ProtocolDefinition {
    return new ProtocolDefinition({ ...this.facts, spine });
  }

  protected substantiveFacts(): unknown {
    return {
      envelopeMinutes: this.envelopeMinutes,
      steps: this.steps.map(serializeStep),
      exams: this.exams,
    };
  }

  // ── commands ──

  /** Create authored work through the same intent boundary as a whole-content revision. */
  static define(input: ProtocolWorkInput & { spine: CatalogSpine }, facts: ProtocolWorkFacts): ProtocolDefinition {
    return ProtocolDefinition.buildWork(input.spine, input, facts);
  }

  /** Apply membership and steps atomically; stored provenance remains entity-owned. */
  setWork(input: ProtocolWorkInput, facts: ProtocolWorkFacts): void {
    const candidate = ProtocolDefinition.buildWork(this.spineFacts, input, facts, this);
    this.steps = candidate.steps;
    this.exams = candidate.exams;
    this.envelopeMinutes = candidate.envelopeMinutes;
    this.occurrenceHigh = candidate.occurrenceHigh;
  }

  private static buildWork(
    spine: CatalogSpine,
    input: ProtocolWorkInput,
    facts: ProtocolWorkFacts,
    previous?: ProtocolDefinition,
  ): ProtocolDefinition {
    const exams = distinctExams(input.exams);
    const sequence = memberIdSequence(OCCURRENCE_PREFIX, previous?.occurrenceHigh ?? 0);
    const flattened = assignMemberIds(
      input.steps.flatMap((step) => step.occurrences),
      previous ? memberIds(previous.occurrences) : memberIds(input.steps.flatMap((step) => step.occurrences)),
      sequence,
      OCCURRENCES,
    );
    let cursor = 0;
    const candidate = new ProtocolDefinition({
      spine,
      envelopeMinutes: input.envelopeMinutes,
      steps: input.steps.map((step) => ({
        title: step.title,
        placement: serializePlacement(placementFor(step)),
        occurrences: step.occurrences.map((occurrence) => ({
          ...occurrence,
          id: flattened[cursor++]?.id,
          justifications: [AUTHORED],
        })),
      })),
      exams,
      occurrenceHighWaterMark: sequence.high,
    });
    candidate.assertReferencesActive(facts);
    if (previous && input.envelopeMinutes !== previous.envelopeMinutes) {
      candidate.setEnvelopeDuration(input.envelopeMinutes);
    }
    return candidate;
  }

  /** Drop exactly the named answers; everything else the protocol holds is untouched. */
  clearParameterValues(values: readonly ProtocolValueRef[]): void {
    const named = (occurrenceId: MemberId, plannedTrialId: PlannedTrialId | undefined, answer: ParameterAnswer) =>
      values.some(
        (value) =>
          value.occurrenceId === occurrenceId &&
          value.plannedTrialId === plannedTrialId &&
          value.source === answer.source &&
          value.parameterId === answer.parameterId,
      );
    this.steps = this.steps.map((step) => ({
      ...step,
      occurrences: step.occurrences.map((occurrence) => ({
        ...occurrence,
        parameterValues: occurrence.parameterValues.filter((answer) => !named(occurrence.id, undefined, answer)),
        ...(occurrence.plannedTrials
          ? {
              plannedTrials: occurrence.plannedTrials.map((trial) => ({
                ...trial,
                values: trial.values.filter((answer) => !named(occurrence.id, trial.id, answer)),
              })),
            }
          : {}),
      })),
    }));
  }

  /** An author may lower the reservation only when all current work still fits. */
  setEnvelopeDuration(minutes: number): void {
    assertDuration(minutes);
    const candidate = new ProtocolDefinition({ ...this.facts, envelopeMinutes: minutes });
    if (candidate.durationMinutes !== minutes) {
      throw new InvalidCatalogDefinitionError("The envelope must contain all protocol steps", {
        field: "envelopeMinutes",
      });
    }
    this.envelopeMinutes = minutes;
  }

  /**
   * Replace the timed structure wholesale.
   *
   * ONE SETTER RATHER THAN NINE COMMANDS (`addStep`, `moveOccurrence`,
   * `resizeStep` …). The one cross-member rule inside the timed
   * structure is that an exam-justified occurrence cannot be deleted, and this
   * enforces it ON THE RESULT: whatever steps it is handed, an exam left
   * without its required diagnostic is rejected. Nine commands do not make
   * that rule stronger, they give it nine entry points. It is also the shape
   * the drag-and-drop builder already produces — it mutates form state and
   * persists nothing per drag.
   *
   * Cost: nudging one duration sends the whole structure. Fine for a builder
   * form; not fine for a future patch-style API.
   */
  setSteps(
    steps: readonly ProtocolStepInput[],
    facts: ReferenceAvailabilityFacts,
    authoredEnvelopeMinutes?: number,
  ): void {
    this.assertReferencesActive(facts);
    const known = memberIds(this.occurrences);
    const stored = new Map(this.occurrences.map((occurrence) => [occurrence.id, occurrence]));
    const sequence = this.sequence();
    // Identities are carried across the setter, never reminted: an order
    // materialized from this protocol cites an occurrence id, and a setter
    // that treated its argument as fresh content would break every such
    // reference silently.
    const flattened = assignMemberIds(
      steps.flatMap((step) => step.occurrences),
      known,
      sequence,
      OCCURRENCES,
    );
    let cursor = 0;
    const next: PlacedProtocolStep[] = steps
      .map((step) => ({
        placement: placementFor(step),
        ...(step.title === undefined ? {} : { title: step.title }),
        occurrences: step.occurrences.map(() => {
          const occurrence = flattened[cursor++] as ProtocolOccurrenceInput & { id: MemberId };
          return {
            ...occurrence,
            parameterValues: occurrence.parameterValues ?? [],
            // Justifications come from stored state, never from the caller.
            justifications: stored.get(occurrence.id)?.justifications ?? [AUTHORED],
          };
        }),
      }))
      .sort((a, b) => a.placement.offsetMinutes - b.placement.offsetMinutes);

    // A whole-content edit may carry the unchanged reservation while growing work.
    // Only an explicitly changed envelope is a request to set that exact value.
    const candidate = new ProtocolDefinition({
      ...this.facts,
      steps: next.map(serializeStep),
      envelopeMinutes: authoredEnvelopeMinutes ?? this.envelopeMinutes,
    });
    if (authoredEnvelopeMinutes !== undefined && authoredEnvelopeMinutes !== this.envelopeMinutes) {
      candidate.setEnvelopeDuration(authoredEnvelopeMinutes);
    }
    this.steps = candidate.steps;
    this.envelopeMinutes = candidate.durationMinutes;
    this.occurrenceHigh = sequence.high;
  }

  private sequence(): MemberIdSequence {
    return memberIdSequence(OCCURRENCE_PREFIX, this.occurrenceHigh);
  }
}

/**
 * What completeness knows of one procedure definition.
 *
 * `name` is carried for the MESSAGE, not the check. A violation is
 * read by a clinician deciding what to fix, and `'https://…/ActivityDefinition/
 * transcranial-photobiomodulation' requires parameter 'p-1'` tells them nothing
 * they can act on — the ids are how the system matches, never how it explains.
 */
export interface ProcedureParameterFacts {
  /** The procedure's display name. */
  name: string;
  /** The current effective declarations: the procedure's own plus every device kind's. */
  parameters: readonly ProtocolParameterDeclaration[];
}

/** Where one stored answer sits: on the occurrence itself, or inside one of its planned trials. */
export interface ProtocolValueRef {
  occurrenceId: MemberId;
  plannedTrialId?: PlannedTrialId;
  source: ParameterAnswer["source"];
  parameterId: MemberId;
}

export type ProtocolValueReason = "parameter-removed" | "option-removed" | "invalid";

/** A stored answer the current procedure does not accept, located so a caller can clear it. */
export interface ProtocolValueViolation extends CompletenessViolation {
  memberId: MemberId;
  plannedTrialId?: PlannedTrialId;
  answer: ParameterAnswer;
  /** The procedure's name, with the occurrence's qualifier. */
  procedure: string;
  /** 1-based position of the planned trial; absent for the occurrence's own answer. */
  trial?: number;
  /** Absent when the parameter is gone from the current revision. */
  parameterLabel?: string;
  reason: ProtocolValueReason;
  /** Why an `invalid` value is invalid, e.g. "must be at most 20". */
  detail?: string;
}

export interface ProtocolParameterDeclaration {
  source: ParameterAnswer["source"];
  parameterId: MemberId;
  label: string;
  value: ParameterValue;
}

/**
 * Why a protocol cannot yet produce a complete order set.
 *
 * A FREE FUNCTION, not a method, and it throws nothing. It needs the
 * definitions its occurrences cite, and a method that only works if you hand
 * it the world is not the aggregate answering for itself. Two callers, one
 * rule: the handler calls it before commit and decides to reject;
 * materialization calls the same function and decides for itself.
 */
export function protocolCompletenessViolations(
  protocol: { occurrences: readonly ProtocolOccurrence[] },
  definitions: ReadonlyMap<string, ProcedureParameterFacts>,
): readonly (CompletenessViolation | ProtocolValueViolation)[] {
  const violations: (CompletenessViolation | ProtocolValueViolation)[] = [];
  for (const occurrence of protocol.occurrences) {
    const definition = definitions.get(occurrence.definition);
    if (!definition) {
      violations.push({
        code: "unknown-definition",
        message: `No definition was supplied for '${occurrence.definition}'`,
        memberId: occurrence.id,
        collection: OCCURRENCES,
      });
      continue;
    }
    const name = occurrence.qualifier ? `${definition.name} (${occurrence.qualifier})` : definition.name;
    const check = (
      answers: readonly ParameterAnswer[],
      where: string,
      trial?: { id: PlannedTrialId; position: number },
    ) => {
      for (const answer of answers) {
        const declaration = definition.parameters.find(
          (candidate) => candidate.source === answer.source && candidate.parameterId === answer.parameterId,
        );
        const problem = declaration && answeredValueProblem(declaration.value, answer.value);
        if (declaration && !problem) continue;
        const declared = declaration?.value;
        const removedOption =
          declared?.kind === "Choice" &&
          answer.value.kind === "Choice" &&
          answer.value.selected.some((id) => !declared.options.some((option) => option.id === id));
        violations.push({
          code: declaration ? "invalid-parameter-value" : "undeclared-parameter",
          message: !declaration
            ? `${where}: has a value for a removed parameter`
            : removedOption
              ? `${where}: ${declaration.label} uses a removed option`
              : `${where}: ${declaration.label} ${problem}`,
          memberId: occurrence.id,
          collection: OCCURRENCES,
          ...(trial === undefined ? {} : { plannedTrialId: trial.id, trial: trial.position }),
          answer,
          procedure: name,
          ...(declaration ? { parameterLabel: declaration.label } : {}),
          reason: !declaration ? "parameter-removed" : removedOption ? "option-removed" : "invalid",
          ...(declaration && !removedOption && problem ? { detail: problem } : {}),
        });
      }
    };
    check(occurrence.parameterValues, name);
    for (const [index, trial] of (occurrence.plannedTrials ?? []).entries())
      check(trial.values, `${name}, planned trial ${index + 1}`, { id: trial.id, position: index + 1 });
  }
  return violations;
}
